/**
 * The filter machinery the pages share: define a filter, get a modal control,
 * a URL param, an options list and a predicate.
 *
 * The CEO asked for "a filter button that opens a modal for doing any filter we
 * add", so the cost of adding the next filter is what this module is designed
 * around. A filter is one entry in a `FilterDef[]` -- it says where to read the
 * row's value and how to order the options. Nothing here knows what a person or
 * a month is, and nothing below hardcodes a list of either: every option comes
 * from the rows actually on the page, so a filter can never offer a month the
 * project has no hours in.
 *
 * Semantics, chosen so a filter cannot quietly mean something else as filters
 * are added: OR within one filter (August OR September), AND across filters
 * (August, AND only Ada). That is what "only show me Aug 2026 or specific
 * people" means when both are set at once.
 */

export interface FilterDef<Row> {
  /** Stable key. Becomes the URL param `?f_<key>=`, so do not rename lightly. */
  key: string;
  /** What the modal and the active-filter line call it. */
  label: string;
  /**
   * The row's value for this filter. Null or blank means the row carries no
   * value, and such rows are never offered as an option -- an empty checkbox
   * labelled nothing is not a filter anyone can use.
   */
  valueOf: (row: Row) => string | null | undefined;
  /**
   * What the options are ORDERED by, read from a row that carries the value.
   *
   * Separate from `valueOf` because the two differ exactly where it matters:
   * months display as "April 2024" and must sort on `period_start`. Sorting
   * month names alphabetically puts April 2024 before December 2023, a bug this
   * codebase has already shipped twice.
   */
  sortBy?: (row: Row) => string | number | null | undefined;
  /** Largest sort key first. Months want this; names do not. */
  descending?: boolean;
  /** One sentence in the modal saying what the filter covers. */
  help?: string;
}

/** Selected values per filter key. An absent or empty entry means "all". */
export type FilterValues = Record<string, string[]>;

export interface FilterOption {
  value: string;
  /**
   * How many rows on the page carry it, so a one-row option is visibly one.
   *
   * OPTIONAL, because only a filter applied here can honestly answer it.
   * `buildFilterOptions` counts the rows it was handed; a filter the SERVER
   * applies gets its options from the API's own list and has no idea how many
   * rows sit behind each one. Printing 0 there would not be a smaller number,
   * it would be a wrong one -- so an option without a count renders without a
   * count.
   */
  count?: number;
}

/**
 * The options each filter can offer, derived from the rows on the page.
 *
 * Never a fixed list: a hardcoded month or name list is how a filter comes to
 * offer a choice that returns nothing, or to omit a month the project actually
 * has hours in.
 */
export function buildFilterOptions<Row>(
  rows: readonly Row[],
  defs: readonly FilterDef<Row>[],
): Record<string, FilterOption[]> {
  const out: Record<string, FilterOption[]> = {};

  for (const def of defs) {
    const seen = new Map<string, { count: number; sort: string | number }>();
    for (const row of rows) {
      const raw = def.valueOf(row);
      if (raw === null || raw === undefined) continue;
      const value = String(raw);
      if (value === "") continue;
      const existing = seen.get(value);
      if (existing) {
        existing.count += 1;
        continue;
      }
      const sortRaw = def.sortBy?.(row);
      seen.set(value, {
        count: 1,
        // Falling back to the value itself keeps a def without `sortBy`
        // alphabetical, which is what a list of names wants.
        sort: sortRaw === null || sortRaw === undefined ? value : sortRaw,
      });
    }

    out[def.key] = [...seen.entries()]
      .sort((a, b) => {
        const [aValue, aMeta] = a;
        const [bValue, bMeta] = b;
        const cmp =
          typeof aMeta.sort === "number" && typeof bMeta.sort === "number"
            ? aMeta.sort - bMeta.sort
            : String(aMeta.sort).localeCompare(String(bMeta.sort));
        // Ties broken on the value so the order is stable rather than
        // dependent on which row happened to appear first.
        return (def.descending ? -cmp : cmp) || aValue.localeCompare(bValue);
      })
      .map(([value, meta]) => ({ value, count: meta.count }));
  }

  return out;
}

/** Rows that pass every active filter. An empty selection filters nothing. */
export function applyFilters<Row>(
  rows: readonly Row[],
  defs: readonly FilterDef<Row>[],
  values: FilterValues,
): Row[] {
  const active = defs
    .map((def) => ({ def, selected: new Set(values[def.key] ?? []) }))
    .filter((entry) => entry.selected.size > 0);
  if (active.length === 0) return [...rows];

  return rows.filter((row) =>
    active.every(({ def, selected }) => {
      const raw = def.valueOf(row);
      // A row with no value for an active filter is excluded, not passed
      // through. "Only show me Ada" must not also return the unattributed rows.
      if (raw === null || raw === undefined) return false;
      return selected.has(String(raw));
    }),
  );
}

/**
 * How many filters are ON -- filters, not values.
 *
 * The count on the button answers "is anything filtering this table", so three
 * people picked in one filter is one, matching the one line it adds to the
 * summary. Counting values would read as three filters.
 */
export function activeFilterCount(values: FilterValues): number {
  return Object.values(values).filter((list) => list.length > 0).length;
}

/**
 * The active filters in words, for the line that sits above a filtered table.
 *
 * A count alone ("2 filters") does not tell a reader which months they are
 * looking at, and a filtered total with an unnamed filter is exactly the way to
 * misread a number.
 */
export function summariseFilters<Row>(
  defs: readonly FilterDef<Row>[],
  values: FilterValues,
): string[] {
  const out: string[] = [];
  for (const def of defs) {
    const selected = values[def.key] ?? [];
    if (selected.length === 0) continue;
    // Up to two values named in full; beyond that the count, because a line
    // naming eleven people is no longer a label.
    const shown = selected.length <= 2 ? selected.join(", ") : `${selected.length} selected`;
    out.push(`${def.label}: ${shown}`);
  }
  return out;
}

/** Drops every selection. Used by both Clear controls. */
export function emptyFilterValues<Row>(defs: readonly FilterDef<Row>[]): FilterValues {
  return Object.fromEntries(defs.map((def) => [def.key, []]));
}
